import { useState, useEffect } from 'react'
import { createClient } from '@supabase/supabase-js'
import axios from 'axios'
import Login           from './pages/Login'
import Dashboard       from './pages/Dashboard'
import PanelProfesor   from './pages/PanelProfesor'
import PanelAdmin      from './pages/PanelAdmin'
import Precios         from './pages/Precios'
import ExamenNivel     from './pages/ExamenNivel'
import PagoPlan        from './pages/PagoPlan'
import ConfirmandoPago from './pages/ConfirmandoPago'
import PlanVencido     from './pages/PlanVencido'
import Landing         from './pages/Landing'
import EmailConfirmado from './pages/EmailConfirmado'

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
)

const API = process.env.REACT_APP_API_URL

const PRECIOS_USD = {
  trial:        0,
  monthly:      119.99,
  eightmonths:  719.99, // EspañolGo Advanced -- dura 7 meses (ver pagos.js)
  annual:       1199
}

export default function App() {
  const [usuario,     setUsuario]     = useState(null)
  const [rol,         setRol]         = useState(null)
  const [profesor,    setProfesor]    = useState(null)
  const [cargando,    setCargando]    = useState(true)
  const [pantalla,    setPantalla]    = useState('landing')
  const [planElegido, setPlanElegido] = useState(null)
  // true cuando se llego a Login.jsx por el boton "Get Started" del landing
  // -- ese boton es solo para estudiantes nuevos, asi que Login.jsx entra
  // directo al formulario de estudiante, sin mostrar la lista de roles.
  const [entrarSoloEstudiante, setEntrarSoloEstudiante] = useState(false)
  const [nivelExamen, setNivelExamen] = useState(null)
  const [datosRevisionExamen, setDatosRevisionExamen] = useState(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const hash   = new URLSearchParams(window.location.hash.replace('#', '?'))

    const esConfirmacion = params.get('type') === 'signup'  ||
                           hash.get('type')   === 'signup'  ||
                           params.get('token_hash') !== null

    if (esConfirmacion) {
      const tokenHash = params.get('token_hash')
      if (tokenHash) {
        supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'signup' })
          .catch(() => {})
      }
      setPantalla('email-confirmado')
      setCargando(false)
      return
    }

    // Si el estudiante vuelve de aprobar el pago en PayPal, esto recarga
    // la pagina por completo. Como la sesion de Supabase persiste en el
    // navegador, la recuperamos para no perder el login y lo mandamos a
    // la pantalla que confirma el pago.
    if (params.get('pago') === '1') {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          setUsuario(session.user)
          setRol('estudiante')
          guardarRolPreferido('estudiante')
          setPantalla('pago-confirmando')
        }
        window.history.replaceState({}, '', window.location.pathname)
        setCargando(false)
      })
      return
    }
    if (params.get('pago') === 'cancelado') {
      window.history.replaceState({}, '', window.location.pathname)
    }

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        setUsuario(session.user)
        const rolRestaurado = await restaurarRol(session.user, params.get('admin') === '1')
        registrarAcceso(session.user.id)
        // Antes aqui se hacia setPantalla('app') directo, sin revisar nada
        // mas -- cualquiera con una cuenta creada (sin pagar nunca, o con
        // la suscripcion vencida) entraba con solo refrescar la pagina,
        // porque la sesion de Supabase persiste sola en el navegador. Ver
        // entrarSegunEstadoPlan() mas abajo: es la misma revision que ya
        // se hacia al iniciar sesion, ahora tambien aqui.
        await entrarSegunEstadoPlan(session.user, rolRestaurado)
      }
      setCargando(false)
    })

    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUsuario(session?.user || null)
      if (!session) {
        setRol(null)
        setProfesor(null)
        setPantalla('landing')
      }
    })
    return () => sub?.subscription?.unsubscribe()
  }, [])

  // Guarda en este tab/pestaña (sessionStorage) cual rol eligio el usuario
  // al entrar. Esto es solo una PREFERENCIA para restaurarRol() -- nunca
  // otorga el rol por si sola. Si alguien la manipulara a mano para decir
  // "admin" o "profesor", igual tiene que pasar la verificacion real contra
  // el backend (esAdmin/esProfesor) mas abajo, asi que no hay forma de
  // escalar privilegios cambiando este valor.
  function guardarRolPreferido(rolElegido) {
    try { sessionStorage.setItem('espanolgo_rol_preferido', rolElegido) } catch (err) {}
  }
  function leerRolPreferido() {
    try { return sessionStorage.getItem('espanolgo_rol_preferido') } catch (err) { return null }
  }

  // Al recargar la pagina, la sesion de Supabase persiste sola, pero el rol
  // (admin/profesor/estudiante) solo se calculaba al iniciar sesion y vivia
  // en memoria — se perdia con el refresh. El bug: esto SIEMPRE volvia a
  // preguntar "es profesor este correo?" y si la respuesta era si, mandaba
  // al panel de profesor sin importar que rol hubiera elegido el usuario al
  // entrar -- por eso una cuenta que es profesor Y quiere explorar como
  // Estudiante terminaba de vuelta en su panel de profesor apenas
  // refrescaba, incluso en medio de una leccion. Ahora respetamos el rol
  // que el usuario eligio explicitamente la ultima vez (guardado en
  // sessionStorage), y solo si nunca eligio nada (o eligio profesor/admin)
  // volvemos a verificar contra el backend, igual que en Login.jsx.
  async function restaurarRol(user, quiereAdmin) {
    const preferido = leerRolPreferido()
    try {
      if (quiereAdmin || preferido === 'admin') {
        const res = await axios.get(`${API}/curriculo/admin/verificar/${user.email}`)
        if (res.data?.esAdmin) { setRol('admin'); return 'admin' }
      }
      if (preferido !== 'estudiante') {
        const res = await axios.get(`${API}/curriculo/profesor/verificar/${user.email}`)
        if (res.data?.esProfesor) { setRol('profesor'); setProfesor(res.data.profesor); return 'profesor' }
      }
    } catch (err) {
      console.error('Error restaurando rol:', err)
    }
    setRol('estudiante')
    return 'estudiante'
  }

  async function registrarAcceso(userId) {
    try {
      await axios.post(API + '/progreso/registrar-acceso', { usuario_id: userId })
    } catch (err) {
      console.error('Error registrando acceso:', err)
    }
  }

  async function handleLogout() {
    await supabase.auth.signOut()
    setUsuario(null); setRol(null); setProfesor(null)
    try { sessionStorage.removeItem('espanolgo_rol_preferido') } catch (err) {}
    setPantalla('landing')
  }

  async function handleLogin(user, rolUsuario, datosExtra) {
    setUsuario(user)
    setRol(rolUsuario || 'estudiante')
    guardarRolPreferido(rolUsuario || 'estudiante')
    if (datosExtra) setProfesor(datosExtra)
    registrarAcceso(user.id)

    // Si eligio un plan pago nuevo (desde Precios), siempre va primero a
    // pagarlo, sin importar el estado de un plan anterior.
    if (planElegido && planElegido.id !== 'trial' && (rolUsuario || 'estudiante') === 'estudiante') {
      setPantalla('pago')
      return
    }

    // El plan trial se activa aqui. Esto cubre dos caminos: 1) el de
    // siempre, cuando se elige el plan ANTES de registrarse (Login.jsx ya
    // lo activa tambien ahi, pero llamarlo de nuevo aqui no hace daño -- el
    // backend no deja pisar un plan que ya existe, ver pagos.js); y 2) el
    // boton "Get Started" del landing, donde la cuenta se crea PRIMERO y el
    // plan se elige DESPUES de confirmar el correo -- ahi este es el UNICO
    // lugar donde se activa, porque planElegido no sobrevive el refresco
    // de pagina que pasa al confirmar el correo.
    if (planElegido?.id === 'trial' && (rolUsuario || 'estudiante') === 'estudiante') {
      try {
        await axios.post(API + '/pagos/iniciar-prueba', { usuario_id: user.id })
      } catch (err) {
        console.error('Error iniciando prueba gratis:', err)
      }
    }

    // Si no viene a pagar nada nuevo, revisamos si su prueba/plan actual
    // sigue vigente antes de dejarlo entrar.
    await entrarSegunEstadoPlan(user, rolUsuario || 'estudiante')
  }

  // ── Unica puerta de entrada a la plataforma ('app'): revisa si el plan
  //    del estudiante sigue vigente antes de dejarlo pasar. Esto SIEMPRE
  //    hay que llamarlo antes de mostrar 'app' — nunca hacer
  //    setPantalla('app') directamente desde otro lado (mismo patron que
  //    ya usa EnglishBridge en App.js). Antes esto solo se revisaba aqui,
  //    en handleLogin(); el efecto que restaura la sesion al recargar la
  //    pagina hacia setPantalla('app') derecho, sin pasar por esta
  //    revision — cualquiera con una cuenta creada (sin pagar nunca, o
  //    con la suscripcion vencida) entraba con solo refrescar la pagina.
  //    Solo aplica a estudiantes — profesores y admin no dependen de un
  //    plan pago.
  async function entrarSegunEstadoPlan(user, rol) {
    if (rol === 'estudiante' && user?.id) {
      try {
        const res = await axios.get(`${API}/pagos/estado/${user.id}`)
        const datos = res.data?.datos
        // Las cuentas de administrador (es_admin=true en la base de datos)
        // nunca se bloquean por el estado del plan, sin importar que rol
        // se haya elegido al entrar. Asi el mismo correo que se usa para
        // administrar la plataforma tambien puede entrar como "Estudiante"
        // a probar, aunque su suscripcion este vencida o sin plan.
        if (!datos?.es_admin) {
          // IMPORTANTE: esto tiene que ser una lista de QUE SI se permite
          // (activa/prueba), no una lista de que se bloquea. Antes se
          // bloqueaba solo si el estado era exactamente 'vencida' o
          // 'sin_plan' -- pero una cuenta recien creada (que nunca ha
          // pagado) no tiene ese campo en 'sin_plan': la fila en
          // "usuarios" se crea sin tocar suscripcion_estado, asi que queda
          // en null. null no es igual a 'vencida' ni a 'sin_plan', asi que
          // se colaba igual, aunque nunca hubiera pagado nada. Con la
          // lista de permitidos, cualquier valor que no sea explicitamente
          // 'activa' o 'prueba' (null incluido) se bloquea por defecto.
          const estado = datos?.suscripcion_estado
          const permitido = estado === 'activa' || estado === 'prueba'
          if (!permitido) {
            // Antes esto mandaba a la pantalla intermedia "Tu prueba o tu
            // plan ya vencieron" (PlanVencido.jsx) con un boton para ver los
            // planes. Para minimizar pasos, ahora va directo a la pantalla
            // de precios -- un clic menos para volver a pagar.
            setPantalla('precios')
            return
          }
        }
      } catch (err) {
        console.error('Error revisando el estado del plan:', err)
        // Si la consulta falla por un error tecnico, no lo dejamos
        // trabado aqui — lo dejamos pasar y seguimos con el flujo normal.
      }
    }

    setPantalla('app')
  }

  // "Get Started" en el landing: manda directo a crear cuenta de
  // estudiante, SIN elegir plan todavia. Una vez creada la cuenta (o
  // confirmado el correo y vuelto a entrar), entrarSegunEstadoPlan() lo
  // manda solo a la pantalla de precios porque todavia no tiene plan
  // activo -- ahi elige el plan pago o el trial gratis.
  function handleCrearCuenta() {
    setPlanElegido(null)
    setEntrarSoloEstudiante(true)
    setPantalla('login')
  }

  function handleElegirPlan(plan) {
    if (plan.esBack) { setPantalla('landing'); return }
    const planConPrecio = { ...plan, precio_usd: PRECIOS_USD[plan.id] || 0 }
    setPlanElegido(planConPrecio)
    // Siempre pasa primero por registrarse/iniciar sesion — igual que
    // EnglishBridge. El plan trial tambien: se activa justo despues de
    // registrarse (ver Login.jsx), sin pasar por PagoPlan.
    setPantalla('login')
  }

  function handleTerminarExamen(resultado, datosRevision) {
    setNivelExamen(resultado)
    // Escritura y grabaciones de voz del examen -- se retienen aqui hasta
    // que el estudiante se registre (Login.jsx los envia entonces a
    // revision docente), igual que en EnglishBridge.
    setDatosRevisionExamen(datosRevision || null)
    setPantalla('precios')
  }

  if (cargando) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontSize:'16px', color:'#6B7280' }}>
      Cargando EspañolGo...
    </div>
  )

  if (pantalla === 'email-confirmado') return <EmailConfirmado />

  if (pantalla === 'landing') return (
    <Landing
      onEmpezarExamen={() => setPantalla('examen')}
      onVerPlanes={() => setPantalla('precios')}
      onLogin={() => { setEntrarSoloEstudiante(false); setPantalla('login') }}
      onCrearCuenta={handleCrearCuenta}
    />
  )

  if (pantalla === 'examen') return (
    <ExamenNivel
      onTerminar={handleTerminarExamen}
      onSaltar={() => setPantalla('precios')}
      onVolver={() => setPantalla('landing')}
    />
  )

  if (pantalla === 'precios') return (
    <Precios onContinuar={handleElegirPlan} nivelExamen={nivelExamen} />
  )

  if (pantalla === 'pago' && planElegido) return (
    <PagoPlan
      plan={planElegido}
      usuario={usuario}
      onCancelar={() => setPantalla('precios')}
    />
  )

  if (pantalla === 'pago-confirmando') return (
    <ConfirmandoPago
      usuario={usuario}
      onListo={() => { setPlanElegido(null); entrarSegunEstadoPlan(usuario, rol || 'estudiante') }}
    />
  )

  if (pantalla === 'plan-vencido') return (
    <PlanVencido
      onElegirPlan={() => setPantalla('precios')}
      onCerrarSesion={handleLogout}
    />
  )

  if (pantalla === 'login') return (
    <Login onLogin={handleLogin} planElegido={planElegido} datosRevision={datosRevisionExamen} onVolver={() => setPantalla('landing')} soloEstudiante={entrarSoloEstudiante} />
  )

  if (!usuario) return (
    <Landing
      onEmpezarExamen={() => setPantalla('examen')}
      onVerPlanes={() => setPantalla('precios')}
      onLogin={() => { setEntrarSoloEstudiante(false); setPantalla('login') }}
      onCrearCuenta={handleCrearCuenta}
    />
  )

  if (rol === 'profesor' && profesor) return (
    <PanelProfesor profesor={profesor} onLogout={handleLogout} />
  )

  if (rol === 'admin') return (
    <PanelAdmin usuario={usuario} onLogout={handleLogout} standalone={true} />
  )

  return <Dashboard usuario={usuario} onLogout={handleLogout} />
}
